import { Directive, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { GenesysUserPermissionService } from 'common/store/genesys-cloud/users/services/user-permission.service';
import { UserPermission } from 'common/store/genesys-cloud/users/models/user-permission.enum';
import { PermissionMatchMode } from '../models/permission-match-mode.model';

@Directive()
export abstract class UserHasPermissionDirectiveBase implements OnDestroy {
  private readonly onDestroySubject = new Subject<void>();
  private _hasView = false;

  protected constructor(protected readonly templateRef: TemplateRef<any>,
                        protected readonly viewContainer: ViewContainerRef,
                        protected readonly permissionService: GenesysUserPermissionService) {
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  protected abstract get matchMode(): PermissionMatchMode;

  protected setPermissions(permissions: UserPermission | UserPermission[]): void {
    const expectedPermissions: UserPermission[] = Array.isArray(permissions) ? permissions : [permissions];

    this.onDestroySubject.next(); // Destroy existing subscription
    this.queryUserPermissionByMatchMode$(expectedPermissions).pipe(
      map(hasPermission => this.createOrDestroyView(hasPermission)),
      takeUntil(this.onDestroySubject)
    ).subscribe();
  }

  private createOrDestroyView(hasPermission: boolean) {
    if (hasPermission && !this._hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this._hasView = true;
    } else if (!hasPermission && this._hasView) {
      this.viewContainer.clear();
      this._hasView = false;
    }
  }

  private queryUserPermissionByMatchMode$(expectedPermissions: UserPermission[]): Observable<boolean> {
    switch (this.matchMode) {
      case 'any':
        return this.permissionService.hasAnyPermission$(...expectedPermissions);
      case 'all':
        return this.permissionService.hasAllPermissions$(...expectedPermissions);
      default: {
        return throwError(new Error(`Unrecognizable match mode: ${this.matchMode}`));
      }
    }
  }
}
